import React, { useEffect, useState } from 'react';
import api from '../api';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#facc15',
    bg: '#0f172a'
};

const EquipmentManager = () => {
    // --- STATE ---
    const [activeTab, setActiveTab] = useState('registry'); 
    const [myCompanyId, setMyCompanyId] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Data State
    const [registryData, setRegistryData] = useState([]); 
    const [myAssetsData, setMyAssetsData] = useState([]); 
    const [loading, setLoading] = useState(true);
    
    // Registry View State
    const [expandedRow, setExpandedRow] = useState(null);
    const [filter, setFilter] = useState('');

    // --- FORMS ---
    const [newTool, setNewTool] = useState({ 
        name: '', description: '', 
        last_inspection: '', next_inspection: '', 
        last_service: '', next_service: '', 
        photo: null 
    });
    
    const [editingTool, setEditingTool] = useState(null);
    const [uploadingForToolId, setUploadingForToolId] = useState(null);
    const [toolDoc, setToolDoc] = useState({ title: '', file: null, expiry: '' });

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        loadData();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // 1. Get User Info
            const userRes = await api.get('users/me/');
            let cId = userRes.data.company_details?.id;

            // Manual ID Lookup for Superusers
            if (!cId && (userRes.data.is_superuser || userRes.data.is_site_owner)) {
                const compsRes = await api.get('companies/');
                const companyName = userRes.data.company; 
                let foundComp = compsRes.data.find(c => c.name === companyName);
                if (!foundComp) foundComp = compsRes.data.find(c => c.type === 'OWNER');
                if (foundComp) cId = foundComp.id;
            }
            setMyCompanyId(cId);

            // Helper to handle pagination/arrays
            const normalizeData = (res) => {
                if (Array.isArray(res.data)) return res.data;
                if (res.data && Array.isArray(res.data.results)) return res.data.results;
                return [];
            };

            // 2. Fetch Registry (FIX: Added timestamp to bypass browser cache)
            const allRes = await api.get(`contractor/equipment/?t=${Date.now()}`); 
            setRegistryData(normalizeData(allRes));

            // 3. Fetch My Assets (FIX: Added timestamp)
            if (cId) {
                const myRes = await api.get(`companies/${cId}/equipment/?t=${Date.now()}`);
                setMyAssetsData(normalizeData(myRes));
            } else {
                setMyAssetsData([]);
            }

        } catch (err) { console.error(err); } 
        finally { setLoading(false); }
    };

    // --- SHARED ACTIONS ---
    const handleStatusChange = async (id, newStatus) => {
        try {
            await api.post(`contractor/equipment/${id}/set_status/`, { status: newStatus });
            loadData();
        } catch (err) { alert("Failed to update status"); loadData(); } // Reload if failed to clear ghost items
    };

    const handleAddTool = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('name', newTool.name);
        formData.append('description', newTool.description);
        if (newTool.last_inspection) formData.append('last_inspection_date', newTool.last_inspection);
        if (newTool.next_inspection) formData.append('next_inspection_due', newTool.next_inspection);
        if (newTool.last_service) formData.append('last_service_date', newTool.last_service);
        if (newTool.next_service) formData.append('next_service_date', newTool.next_service);
        if (newTool.photo) formData.append('photo', newTool.photo);

        try {
            await api.post('contractor/equipment/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            alert("✅ Asset Added!");
            setNewTool({ name: '', description: '', last_inspection: '', next_inspection: '', last_service: '', next_service: '', photo: null });
            loadData(); 
        } catch (err) { alert("Failed to add equipment"); }
    };

    const handleUpdateTool = async (e) => {
        e.preventDefault();
        try {
            await api.patch(`contractor/equipment/${editingTool.id}/`, {
                description: editingTool.description,
                last_inspection_date: editingTool.last_inspection_date || null,
                next_inspection_due: editingTool.next_inspection_due || null,
                last_service_date: editingTool.last_service_date || null,
                next_service_date: editingTool.next_service_date || null,
                status: editingTool.status 
            });
            alert("✅ Asset Updated!");
            setEditingTool(null);
            loadData();
        } catch (err) { alert("Update failed"); }
    };

    const handleDeleteTool = async (id) => {
        if(window.confirm("Delete this asset?")) {
            try {
                await api.delete(`contractor/equipment/${id}/`);
                
                // OPTIMISTIC UPDATE (Remove from UI immediately)
                const filterFn = (prev) => Array.isArray(prev) ? prev.filter(e => e.id !== id) : [];
                setRegistryData(filterFn);
                setMyAssetsData(filterFn);
                
                // Then reload data to be sure
                loadData();
            } catch (err) { alert("Delete failed"); }
        }
    };

    const handleUploadToolDoc = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('title', toolDoc.title);
        formData.append('file', toolDoc.file);
        if (toolDoc.expiry) formData.append('expiry_date', toolDoc.expiry);

        try {
            await api.post(`contractor/equipment/${uploadingForToolId}/upload-doc/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            alert("✅ Document attached!");
            setToolDoc({ title: '', file: null, expiry: '' });
            setUploadingForToolId(null);
            loadData();
        } catch (err) { alert("Failed to upload document"); }
    };

    const handleDeleteToolDoc = async (toolId, docId) => {
        if(!window.confirm("Delete document?")) return;
        try {
            await api.delete(`contractor/equipment/${toolId}/delete-doc/${docId}/`);
            loadData();
        } catch (err) { alert("Delete failed"); }
    };

    // --- FILTERING ---
    const registryFiltered = (Array.isArray(registryData) ? registryData : []).filter(e => 
        e.name.toLowerCase().includes(filter.toLowerCase()) || 
        (e.company_name || '').toLowerCase().includes(filter.toLowerCase())
    );

    // --- HELPERS ---
    const toggleRow = (id) => setExpandedRow(expandedRow === id ? null : id);

    const getStatusStyle = (status) => {
        switch(status) {
            case 'OPERATIONAL': return { color: THEME.success, bg: 'rgba(16, 185, 129, 0.1)' };
            case 'REJECTED': return { color: THEME.danger, bg: 'rgba(239, 68, 68, 0.1)' };
            case 'MAINTENANCE': return { color: THEME.warning, bg: 'rgba(250, 204, 21, 0.1)' };
            default: return { color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.1)' };
        }
    };

    const inputStyle = { width:'100%', padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background:'#020617', color:'white', fontSize:'0.9rem', marginBottom:'10px', boxSizing:'border-box' };

    if (loading) return <div style={{color:'white'}}>Loading Equipment...</div>;

    return (
        <div className="animate-fade-in" style={{padding:'20px'}}>
            
            {/* HEADER & TABS */}
            <div style={{display:'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent:'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom:'20px', gap:'15px'}}>
                <div style={{display:'flex', alignItems:'center', gap:'15px'}}>
                    <h2 style={{color:'white', margin:0}}>🚜 Equipment Manager</h2>
                    <button onClick={loadData} style={{background:'transparent', border:`1px solid ${THEME.border}`, color:THEME.textSec, padding:'6px 12px', borderRadius:'6px', cursor:'pointer'}}>🔄 Refresh</button>
                </div>
                
                {activeTab === 'registry' && (
                    <input 
                        placeholder="Search equipment or company..." 
                        value={filter} 
                        onChange={e => setFilter(e.target.value)}
                        style={{padding:'10px', borderRadius:'6px', border:`1px solid ${THEME.border}`, background: THEME.card, color:'white', width: isMobile ? '100%' : '300px'}}
                    />
                )}
            </div>

            <div style={{display: 'flex', gap: '10px', marginBottom: '25px', background:'#020617', padding:'5px', borderRadius:'8px', border:`1px solid ${THEME.border}`, width: 'fit-content'}}>
                <button onClick={() => setActiveTab('registry')} style={{padding: '8px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize:'0.9rem', fontWeight:600, background: activeTab === 'registry' ? THEME.primary : 'transparent', color: activeTab === 'registry' ? 'white' : THEME.textSec}}>All Register</button>
                <button onClick={() => setActiveTab('my_assets')} style={{padding: '8px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize:'0.9rem', fontWeight:600, background: activeTab === 'my_assets' ? THEME.primary : 'transparent', color: activeTab === 'my_assets' ? 'white' : THEME.textSec}}>Site Assets</button>
            </div>

            {/* TAB 1: ALL REGISTRY */}
            {activeTab === 'registry' && (
                <div style={{background: THEME.card, borderRadius:'12px', border:`1px solid ${THEME.border}`, overflowX:'auto'}}>
                    <table style={{width:'100%', borderCollapse:'collapse', color:'white', fontSize:'0.9rem'}}>
                        <thead style={{background:'#0f172a', borderBottom:`1px solid ${THEME.border}`}}>
                            <tr>
                                <th style={{padding:'15px', textAlign:'left'}}>Company</th>
                                <th style={{padding:'15px', textAlign:'left'}}>Equipment</th>
                                {!isMobile && <th style={{padding:'15px', textAlign:'left'}}>Next Insp.</th>}
                                <th style={{padding:'15px', textAlign:'center'}}>Status</th>
                                <th style={{padding:'15px', textAlign:'right'}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {registryFiltered.map(item => {
                                const style = getStatusStyle(item.status);
                                return (
                                    <React.Fragment key={item.id}>
                                        <tr style={{borderBottom:`1px solid ${THEME.border}`, background: expandedRow === item.id ? '#1e293b' : 'transparent'}}>
                                            <td style={{padding:'15px', color:THEME.textSec}}>{item.company_name}</td>
                                            <td style={{padding:'15px', fontWeight:'bold'}}>{item.name}</td>
                                            {!isMobile && <td style={{padding:'15px', color: new Date(item.next_inspection_due) < new Date() ? THEME.danger : 'white'}}>{item.next_inspection_due || '-'}</td>}
                                            <td style={{padding:'15px', textAlign:'center'}}>
                                                <span style={{padding:'4px 10px', borderRadius:'12px', fontSize:'0.75rem', fontWeight:'bold', color: style.color, background: style.bg, border:`1px solid ${style.color}`}}>
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td style={{padding:'15px', textAlign:'right'}}>
                                                <button onClick={() => toggleRow(item.id)} style={{background:'transparent', border:`1px solid ${THEME.primary}`, color:THEME.primary, padding:'5px 10px', borderRadius:'4px', cursor:'pointer'}}>
                                                    {expandedRow === item.id ? 'Hide' : 'Review'}
                                                </button>
                                            </td>
                                        </tr>
                                        
                                        {/* EXPANDED DETAILS */}
                                        {expandedRow === item.id && (
                                            <tr style={{background:'#0f172a', borderBottom:`1px solid ${THEME.border}`}}>
                                                <td colSpan="5" style={{padding:'20px'}}>
                                                    <div style={{display:'flex', gap:'30px', flexDirection: isMobile ? 'column' : 'row'}}>
                                                        <div style={{width:'150px', height:'150px', background:'#334155', borderRadius:'8px', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>
                                                            {item.photo ? <a href={item.photo} target="_blank" rel="noreferrer"><img src={item.photo} style={{width:'100%', height:'100%', objectFit:'cover'}} alt="Equip" /></a> : 'No Photo'}
                                                        </div>
                                                        <div style={{flex:1}}>
                                                            <h4 style={{marginTop:0, color:THEME.primary}}>Details</h4>
                                                            <p style={{color:THEME.textSec, fontSize:'0.9rem'}}>{item.description || "No description."}</p>
                                                            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', fontSize:'0.85rem', marginBottom:'15px'}}>
                                                                <div>Insp Due: <span style={{color:'white'}}>{item.next_inspection_due || '-'}</span></div>
                                                                <div>Service Due: <span style={{color:'white'}}>{item.next_service_date || '-'}</span></div>
                                                            </div>
                                                            <h4 style={{color:'white', marginBottom:'10px'}}>Documents</h4>
                                                            <div style={{display:'flex', gap:'10px', flexWrap:'wrap'}}>
                                                                {(item.documents || []).length === 0 ? <span style={{color:THEME.textSec, fontSize:'0.85rem'}}>No docs.</span> : (item.documents || []).map(doc => (
                                                                    <a key={doc.id} href={doc.file} target="_blank" rel="noreferrer" style={{display:'flex', alignItems:'center', gap:'5px', padding:'6px 12px', background:THEME.card, border:`1px solid ${THEME.border}`, borderRadius:'6px', textDecoration:'none', color:'white', fontSize:'0.85rem'}}>
                                                                        📄 {doc.title}
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div style={{display:'flex', flexDirection:'column', gap:'10px', minWidth:'150px'}}>
                                                            <h4 style={{marginTop:0, color:'white'}}>Actions</h4>
                                                            {item.status !== 'OPERATIONAL' && <button onClick={() => handleStatusChange(item.id, 'OPERATIONAL')} style={{padding:'10px', background:THEME.success, color:'white', border:'none', borderRadius:'6px', cursor:'pointer'}}>✅ Approve</button>}
                                                            {item.status !== 'REJECTED' && <button onClick={() => handleStatusChange(item.id, 'REJECTED')} style={{padding:'10px', background:'transparent', border:`1px solid ${THEME.danger}`, color:THEME.danger, borderRadius:'6px', cursor:'pointer'}}>❌ Reject</button>}
                                                            {item.status !== 'MAINTENANCE' && <button onClick={() => handleStatusChange(item.id, 'MAINTENANCE')} style={{padding:'10px', background:'transparent', border:`1px solid ${THEME.warning}`, color:THEME.warning, borderRadius:'6px', cursor:'pointer'}}>🛠 Mark Service</button>}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* TAB 2: SITE ASSETS */}
            {activeTab === 'my_assets' && (
                <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap:'30px', width: '100%'}}>
                    
                    {/* ASSET LIST */}
                    <div style={{minWidth: 0}}>
                        {/* Defensive check: Ensure myAssetsData is an array */}
                        {(Array.isArray(myAssetsData) ? myAssetsData : []).map(e => (
                            <div key={e.id} style={{background:'#020617', padding:'15px', marginBottom:'15px', borderRadius:'8px', border:`1px solid ${THEME.border}`}}>
                                <div style={{display:'flex', gap:'15px', alignItems:'flex-start'}}>
                                    <div style={{width:'60px', height:'60px', background:'#1e293b', borderRadius:'4px', overflow:'hidden', flexShrink:0}}>
                                        {e.photo ? <img src={e.photo} style={{width:'100%', height:'100%', objectFit:'cover'}} alt="Equip" /> : <div style={{width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center'}}>📷</div>}
                                    </div>
                                    <div style={{flex:1}}>
                                        <div style={{display:'flex', justifyContent:'space-between'}}>
                                            <div style={{fontWeight:'bold', color:'white'}}>{e.name}</div>
                                            <span style={{padding:'2px 8px', borderRadius:'12px', fontSize:'0.75rem', fontWeight:'bold', ...getStatusStyle(e.status), border:`1px solid ${getStatusStyle(e.status).color}`}}>{e.status}</span>
                                        </div>
                                        <div style={{fontSize:'0.8rem', color:THEME.textSec}}>{e.description}</div>
                                        <div style={{display:'flex', gap:'15px', marginTop:'5px', fontSize:'0.75rem'}}>
                                            <span style={{color: new Date(e.next_inspection_due) < new Date() ? THEME.danger : THEME.textSec}}>Insp Due: {e.next_inspection_due || '-'}</span>
                                            <span style={{color: new Date(e.next_service_date) < new Date() ? THEME.warning : THEME.textSec}}>Service Due: {e.next_service_date || '-'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div style={{marginTop:'15px', paddingTop:'10px', borderTop:`1px dashed ${THEME.border}`}}>
                                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'5px'}}>
                                        <span style={{fontSize:'0.8rem', color:'white', fontWeight:'bold'}}>Documents</span>
                                        <button onClick={() => setUploadingForToolId(e.id)} style={{background:'transparent', border:`1px solid ${THEME.primary}`, color:THEME.primary, fontSize:'0.75rem', padding:'2px 8px', borderRadius:'4px', cursor:'pointer'}}>+ Add Doc</button>
                                    </div>
                                    <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                                        {(e.documents || []).length === 0 && <span style={{fontSize:'0.75rem', color:THEME.textSec, fontStyle:'italic'}}>No docs.</span>}
                                        {(e.documents || []).map(d => (
                                            <div key={d.id} style={{display:'flex', alignItems:'center', gap:'5px', background:'#1e293b', padding:'4px 8px', borderRadius:'4px', fontSize:'0.75rem', border:`1px solid ${THEME.border}`}}>
                                                <a href={d.file} target="_blank" rel="noreferrer" style={{color:'white', textDecoration:'none'}}>{d.title}</a>
                                                <span onClick={() => handleDeleteToolDoc(e.id, d.id)} style={{cursor:'pointer', color:THEME.danger, marginLeft:'5px'}}>×</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div style={{marginTop:'10px', textAlign:'right', display:'flex', gap:'10px', justifyContent:'flex-end'}}>
                                    <button onClick={() => setEditingTool(e)} style={{background:'transparent', border:`1px solid ${THEME.textSec}`, color:THEME.textSec, fontSize:'0.8rem', padding:'4px 8px', borderRadius:'4px', cursor:'pointer'}}>✏️ Edit</button>
                                    <button onClick={() => handleDeleteTool(e.id)} style={{background:'transparent', border:'none', color:THEME.danger, fontSize:'0.8rem', cursor:'pointer'}}>Remove</button>
                                </div>
                            </div>
                        ))}
                        {(myAssetsData || []).length === 0 && <div style={{color:THEME.textSec, fontStyle:'italic'}}>You have no site assets listed.</div>}
                    </div>
                    
                    {/* ADD FORM */}
                    <div style={{background: THEME.card, padding: '20px', borderRadius: '12px', border: `1px solid ${THEME.border}`, height:'fit-content'}}>
                        <h4 style={{color: 'white', marginTop: 0}}>Add Site Asset</h4>
                        <form onSubmit={handleAddTool}>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Asset Name</label>
                            <input type="text" placeholder="e.g. Site Forklift" value={newTool.name} onChange={e => setNewTool({...newTool, name: e.target.value})} style={inputStyle} required />
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Description</label>
                            <textarea rows="2" placeholder="Model, Serial..." value={newTool.description} onChange={e => setNewTool({...newTool, description: e.target.value})} style={inputStyle} />
                            <div style={{display:'grid', gridTemplateColumns: '1fr 1fr', gap:'10px'}}>
                                <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Last Insp.</label><input type="date" value={newTool.last_inspection} onChange={e => setNewTool({...newTool, last_inspection: e.target.value})} style={inputStyle} /></div>
                                <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Next Insp.</label><input type="date" value={newTool.next_inspection} onChange={e => setNewTool({...newTool, next_inspection: e.target.value})} style={inputStyle} /></div>
                            </div>
                            <div style={{display:'grid', gridTemplateColumns: '1fr 1fr', gap:'10px'}}>
                                <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Last Service</label><input type="date" value={newTool.last_service} onChange={e => setNewTool({...newTool, last_service: e.target.value})} style={inputStyle} /></div>
                                <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Next Service</label><input type="date" value={newTool.next_service} onChange={e => setNewTool({...newTool, next_service: e.target.value})} style={inputStyle} /></div>
                            </div>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Photo</label>
                            <input type="file" accept="image/*" onChange={e => setNewTool({...newTool, photo: e.target.files[0]})} style={{...inputStyle, padding:'6px'}} />
                            <button type="submit" style={{width:'100%', padding:'10px', background: THEME.success, color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer'}}>Add Asset</button>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL FOR DOC UPLOAD */}
            {uploadingForToolId && (
                <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.8)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:100}}>
                    <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', width: isMobile ? '90%' : '400px', border:`1px solid ${THEME.border}`}}>
                        <h4 style={{marginTop:0, color:'white'}}>Attach Document</h4>
                        <form onSubmit={handleUploadToolDoc}>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Title</label>
                            <input placeholder="e.g. Safety Cert" value={toolDoc.title} onChange={e => setToolDoc({...toolDoc, title: e.target.value})} style={inputStyle} required />
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Expiry</label>
                            <input type="date" value={toolDoc.expiry} onChange={e => setToolDoc({...toolDoc, expiry: e.target.value})} style={inputStyle} />
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>File</label>
                            <input type="file" onChange={e => setToolDoc({...toolDoc, file: e.target.files[0]})} style={{...inputStyle, padding:'6px'}} required />
                            <div style={{display:'flex', gap:'10px', marginTop:'15px'}}>
                                <button type="button" onClick={() => setUploadingForToolId(null)} style={{flex:1, background:'transparent', border:`1px solid ${THEME.border}`, color:'white', borderRadius:'6px', padding:'8px', cursor:'pointer'}}>Cancel</button>
                                <button type="submit" style={{flex:1, background:THEME.success, border:'none', color:'white', borderRadius:'6px', padding:'8px', cursor:'pointer'}}>Upload</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL FOR EDITING */}
            {editingTool && (
                <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.8)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:100}}>
                    <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', width: isMobile ? '90%' : '500px', border:`1px solid ${THEME.border}`}}>
                        <h4 style={{marginTop:0, color:'white'}}>Edit Asset: {editingTool.name}</h4>
                        <form onSubmit={handleUpdateTool}>
                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Description</label>
                            <textarea rows="2" value={editingTool.description || ''} onChange={e => setEditingTool({...editingTool, description: e.target.value})} style={inputStyle} />
                            
                            <div style={{display:'grid', gridTemplateColumns: '1fr 1fr', gap:'10px'}}>
                                <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Last Insp.</label><input type="date" value={editingTool.last_inspection_date || ''} onChange={e => setEditingTool({...editingTool, last_inspection_date: e.target.value})} style={inputStyle} /></div>
                                <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Next Insp.</label><input type="date" value={editingTool.next_inspection_due || ''} onChange={e => setEditingTool({...editingTool, next_inspection_due: e.target.value})} style={inputStyle} /></div>
                            </div>
                            <div style={{display:'grid', gridTemplateColumns: '1fr 1fr', gap:'10px'}}>
                                <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Last Service</label><input type="date" value={editingTool.last_service_date || ''} onChange={e => setEditingTool({...editingTool, last_service_date: e.target.value})} style={inputStyle} /></div>
                                <div><label style={{color:THEME.textSec, fontSize:'0.7rem'}}>Next Service</label><input type="date" value={editingTool.next_service_date || ''} onChange={e => setEditingTool({...editingTool, next_service_date: e.target.value})} style={inputStyle} /></div>
                            </div>

                            <label style={{display:'block', color:THEME.textSec, fontSize:'0.8rem', marginBottom:'5px'}}>Status</label>
                            <select value={editingTool.status} onChange={e => setEditingTool({...editingTool, status: e.target.value})} style={inputStyle}>
                                <option value="PENDING">Pending</option>
                                <option value="OPERATIONAL">Operational</option>
                                <option value="MAINTENANCE">Maintenance</option>
                                <option value="REJECTED">Rejected</option>
                            </select>

                            <div style={{display:'flex', gap:'10px', marginTop:'15px'}}>
                                <button type="button" onClick={() => setEditingTool(null)} style={{flex:1, background:'transparent', border:`1px solid ${THEME.border}`, color:'white', borderRadius:'6px', padding:'8px', cursor:'pointer'}}>Cancel</button>
                                <button type="submit" style={{flex:1, background:THEME.success, border:'none', color:'white', borderRadius:'6px', padding:'8px', cursor:'pointer'}}>Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EquipmentManager;